import data from "@emoji-mart/data";
import Picker from "@emoji-mart/react";

import { CSSProperties, FC, useEffect, useMemo, useRef, useState } from "react";
import { cn } from "@src/lib/utils/cn";

type Props = {
  style?: CSSProperties;
  onEmojiSelect: (emoji: string) => void;
  enableFrequentEmojiSort?: boolean;
  navPosition?: "top" | "bottom" | "none";
  previewPosition?: "top" | "bottom" | "none";
  skinTonePosition?: "preview" | "search" | "none";
  showFrequentCategory?: boolean;
  autoFocus?: boolean;
};

const categories = [
  "frequent",
  "people",
  "nature",
  "foods",
  "activity",
  "places",
  "objects",
  "symbols",
  "flags",
];
const categoriesNoFrequent = [
  "people",
  "nature",
  "foods",
  "activity",
  "places",
  "objects",
  "symbols",
  "flags",
];

// emoji-mart reads its colours from "r, g, b" custom properties on its
// shadow host; these Opus tokens fill them in.
const RGB_TOKENS = {
  "--rgb-accent": "--color-brand-primary",
  "--rgb-background": "--color-bg-elevated",
  "--rgb-color": "--color-text-primary",
  "--rgb-input": "--color-bg-secondary",
} as const;

// Full-colour properties inherit through the shadow root, so they sit on
// the wrapper's inline style.
const PICKER_STYLE = {
  "--color-border": "var(--color-border-primary)",
  "--color-border-over": "var(--color-border-secondary)",
} as CSSProperties;

// emoji-mart declares these four on :host, which beats inherited values;
// an outer-document rule on the host element wins, hence the descendant
// selectors. The picker sits inside a Popover (or a dialog tab) that
// already draws the surface, so it drops its own shadow.
const PICKER_HOST_CLASSNAME = cn(
  "[&_em-emoji-picker]:[--font-family:var(--font-family-inter)]",
  "[&_em-emoji-picker]:[--font-size:14px]",
  "[&_em-emoji-picker]:[--border-radius:10px]",
  "[&_em-emoji-picker]:[--shadow:none]",
);

// One shared 2D context for the conversions (null when there's no canvas,
// e.g. jsdom), created on first use.
let sharedContext: CanvasRenderingContext2D | null | undefined;
function getContext(): CanvasRenderingContext2D | null {
  if (sharedContext === undefined) {
    sharedContext = document.createElement("canvas").getContext("2d");
  }
  return sharedContext;
}

/** Resolves a colour token to "r, g, b" (tokens are OKLCH; emoji-mart needs RGB). */
export function resolveTokenRgb(token: string): string | undefined {
  if (typeof document === "undefined") return undefined;
  const ctx = getContext();
  if (!ctx) return undefined;
  const probe = document.createElement("span");
  probe.style.color = `var(${token})`;
  probe.style.display = "none";
  document.body.appendChild(probe);
  const color = getComputedStyle(probe).color;
  probe.remove();
  if (!color) return undefined;
  // A colour the canvas can't parse leaves fillStyle untouched; reset it
  // first so that case falls back to emoji-mart's colours, not black.
  ctx.fillStyle = "#000000";
  ctx.fillStyle = color;
  if (ctx.fillStyle === "#000000") return undefined;
  ctx.clearRect(0, 0, 1, 1);
  ctx.fillRect(0, 0, 1, 1);
  const [r, g, b] = ctx.getImageData(0, 0, 1, 1).data;
  return `${r}, ${g}, ${b}`;
}

function isDarkTheme(): boolean {
  return (
    typeof document !== "undefined" &&
    document.documentElement.classList.contains("dark")
  );
}

function resolveRgbVars(): Record<string, string> {
  const vars: Record<string, string> = {};
  for (const [prop, token] of Object.entries(RGB_TOKENS)) {
    const rgb = resolveTokenRgb(token);
    if (rgb) vars[prop] = rgb;
  }
  return vars;
}

// Re-resolves the token colours whenever the app's .dark class (set by
// next-themes) flips. Only the wrapper's CSS variables change, so the
// picker itself never re-renders for a theme switch.
function useTokenRgbVars(): Record<string, string> {
  const [vars, setVars] = useState<Record<string, string>>(resolveRgbVars);
  useEffect(() => {
    const observer = new MutationObserver(() => setVars(resolveRgbVars()));
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });
    return () => observer.disconnect();
  }, []);
  return vars;
}

const EmojiPicker: FC<Props> = ({
  onEmojiSelect,
  style,
  enableFrequentEmojiSort,
  navPosition = "top",
  previewPosition = "bottom",
  skinTonePosition = "preview",
  showFrequentCategory = true,
  autoFocus,
}) => {
  // Each re-render of @emoji-mart/react calls the picker's update(), and an
  // update after mount can leave the default picker's grid empty. So the
  // picker element is memoised on its real options (the select handler
  // goes through a ref), and the theme is read once at mount: picker
  // popovers mount on open, and the token variables on the wrapper keep the
  // colours live if the theme flips while one is open.
  const [theme] = useState(() => (isDarkTheme() ? "dark" : "light"));
  const rgbVars = useTokenRgbVars();
  const onSelectRef = useRef(onEmojiSelect);
  onSelectRef.current = onEmojiSelect;

  const picker = useMemo(
    () => (
      <Picker
        data={data}
        autoFocus={autoFocus}
        theme={theme}
        navPosition={navPosition}
        previewPosition={previewPosition}
        skinTonePosition={skinTonePosition}
        enableFrequentEmojiSort={enableFrequentEmojiSort}
        categories={showFrequentCategory ? categories : categoriesNoFrequent}
        title=""
        emoji="face_with_cowboy_hat"
        onEmojiSelect={(emoji: { native: string }) =>
          onSelectRef.current(emoji.native)
        }
      />
    ),
    [
      autoFocus,
      theme,
      navPosition,
      previewPosition,
      skinTonePosition,
      enableFrequentEmojiSort,
      showFrequentCategory,
    ],
  );

  return (
    <div
      data-testid="emoji-picker"
      className={PICKER_HOST_CLASSNAME}
      style={{ ...PICKER_STYLE, ...rgbVars, ...style } as CSSProperties}
    >
      {picker}
    </div>
  );
};

export default EmojiPicker;
